import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import {
  getUtmParams,
  getBrowserLanguage
} from '@toasttab/shared/src/utils/formUtils'
import { getInitialFormValues } from '@toasttab/shared/src/hooks/useDataCapture'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { KeyValuePair } from '@toasttab/shared/src/interfaces/Interface'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import {
  errorClass,
  fieldClassError,
  fieldClassSelect,
  formatPhoneNumber,
  initialFormState,
  validateEmail,
  validatePhoneInput
} from '@toasttab/main/src/components/Snowflake/RequestDemo/utils/requestDemoFormUtils'
import {
  FormField,
  FormState,
  getFormActionUrl,
  renderHiddenFields
} from './FormField'
import { extractFirstNameLastName } from '@toasttab/shared/src/utils/dataCaptureUtils'
import heapAnalytics from '@toasttab/shared/utils/heapAnalytics'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { FormDisclaimerOptionsRequestDemo } from '@toasttab/shared/src/utils/Contentful/richText'
import { localizeFormError } from '@toasttab/shared/src/utils/formUtils'

interface RequestDemoFormProps {
  formObject: any
}

const RequestDemoForm: React.FC<RequestDemoFormProps> = ({ formObject }) => {
  const { t } = useI18next()
  const formActionURL =
    formObject.formActionURL ?? 'marketo-integration/default/post'
  const [formActionUrlState, setFormActionUrl] = useState(formActionURL)
  const [form, setForm] = useState<FormState>(initialFormState)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({})
  const [region, setRegion] = useState('us')
  const [countryCode, setCountryCode] = useState<string | null>(null)
  const [postalCode, setPostalCode] = useState<string | null>(null)
  const { geoData } = useGeoLocation() as { geoData: any; loading: boolean }
  const [utmParameters, setUtmParameters] = useState({})
  const [isProgressiveForm, setIsProgressiveForm] = useState(false)
  const [isError, setIsError] = useState(false)
  const formCookie = formObject.formCookie ?? 'toastDemoForm'
  const marketoId = formObject.marketoId ?? '1003'
  const isAjax = false
  const chiliPiperRouter = ''
  const entryId = ''
  const marketoProgramName =
    formObject.marketoProgramName ?? 'Website-Demo-Request'
  const thankYouPageURL = formObject.thankYouPageURL ?? ''
  const initExtraValues = {}

  // --- Effects ---
  useEffect(() => {
    if (geoData) {
      setPostalCode(geoData.postal?.code ?? '')
      setCountryCode(geoData.country?.iso_code ?? '')
    }
  }, [geoData])

  useEffect(() => {
    if (window?.location) {
      const currentRegion = window.location.pathname.split('/')[1]
      if (['ca', 'ie', 'uk', 'au', 'es-us', 'zh-us'].includes(currentRegion)) {
        setRegion(currentRegion)
      }
      const formAction = getFormActionUrl(formActionURL)
      if (formAction !== formActionUrlState) {
        setFormActionUrl(formAction)
      }
    }
  }, [])

  useEffect(() => {
    const utmParams = getUtmParams()
    if (Object.keys(utmParams).length > 0) {
      setUtmParameters(utmParams)
    }
  }, [])

  useEffect(() => {
    const progressiveCookie = _helpers.getCookie('progressiveEnabled')
    setIsProgressiveForm(progressiveCookie === 'true')
  }, [])

  useEffect(() => {
    const postalCodeCookie = _helpers.getCookie('_userUpdatedLoc')
    if (postalCodeCookie && !postalCode) {
      setPostalCode(postalCodeCookie)
    }
  }, [postalCode])

  // --- Initial values ---
  const initValues: KeyValuePair = {
    ...initialFormState,
    _mkt_trk: `${_helpers.getCookie('_mkto_trk')}`,
    _mktoReferrer: `${typeof window !== 'undefined' && window.location.href}`,
    appendVariantTestText: '',
    browserLanguage: getBrowserLanguage(),
    company: _helpers.getCookie('company'),
    cR: chiliPiperRouter,
    craftFormLocation: 'NOT SET',
    craftJavascriptStatus: 'Loaded',
    craftRestaurantToast: '',
    eI: '',
    email: _helpers.getCookie('email'),
    fC: formCookie,
    formid: marketoId,
    gae: '',
    isAjax: isAjax,
    mkoFullName: _helpers.getCookie('fullName'),
    mkoProgramName: marketoProgramName,
    munchkinId: '713-DII-842',
    phone: _helpers.getCookie('phonenumber'),
    postalCode: postalCode ?? _helpers.getCookie('_userUpdatedLoc') ?? '',
    qQVersion: '',
    recaptchaValid: 'no',
    referrerURL: `${typeof window !== 'undefined' && window.location.origin}${
      typeof window !== 'undefined' && window.location.pathname
    }`,
    referringPageURLText: `${
      typeof document !== 'undefined' && document.referrer
    }`,
    returnURL: thankYouPageURL ?? '',
    shownCalendly: '',
    toastType: '',
    ...initExtraValues
  }

  useEffect(() => {
    const utmParams = getUtmParams()
    const { initialValues } = getInitialFormValues(
      initValues,
      utmParams,
      initExtraValues
    )
    // Format phone number if it exists in initial values
    const formattedInitialValues = {
      ...initialValues,
      phone: initialValues.phone
        ? formatPhoneNumber(initialValues.phone) ?? initialValues.phone
        : initialValues.phone
    }
    setForm(
      Object.fromEntries(
        Object.entries({
          ...initialFormState,
          ...formattedInitialValues
        }).map(([k, v]) => [k, v ?? ''])
      ) as typeof initialFormState
    )
  }, [])

  // --- Validation ---
  const validate = () => {
    const newErrors: { [key: string]: string } = {}
    const name = form.mkoFullName.trim()
    if (!name) {
      newErrors.mkoFullName = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'mkoFullName'
        )?.fieldLabel
      )
    } else if (
      name.indexOf(' ') === -1 ||
      [' ', '-'].includes(name.charAt(name.length - 1)) ||
      /[^a-zA-Z\u00c0-\u024f\u1e00-\u1eff\- ]/g.test(name)
    ) {
      newErrors.mkoFullName = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'mkoFullName'
        )?.fieldLabel
      )
    }
    if (!form.email) {
      newErrors.email = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'email'
        )?.fieldLabel
      )
    } else if (validateEmail(form.email)) {
      newErrors.email = localizeFormError(
        t('formField.invalid'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'email'
        )?.fieldLabel
      )
    } else {
      // Add user properties to Heap Analytics when email is valid
      // @ts-ignore - Typescript doesn't like setting new properties on window
      const heapHandler = window.heap
      if (typeof window !== 'undefined' && typeof heapHandler !== 'undefined') {
        heapHandler.addUserProperties({
          email: form.email.toLowerCase()
        })
        heapHandler.identify(form.email.toLowerCase())
      }
    }
    if (!form.phone) {
      newErrors.phone = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'phone'
        )?.fieldLabel
      )
    } else if (validatePhoneInput(form.phone, 'us')) {
      newErrors.phone = localizeFormError(
        t('formField.invalid'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'phone'
        )?.fieldLabel
      )
    } else {
      // Format phone number during validation
      const formattedPhone = formatPhoneNumber(form.phone)
      if (formattedPhone !== form.phone) {
        setForm((prev) => ({ ...prev, phone: formattedPhone }))
      }
    }
    if (!form.company)
      newErrors.company = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'company'
        )?.fieldLabel
      )
    if (!form.postalCode)
      newErrors.postalCode = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'postalCode'
        )?.fieldLabel
      )
    if (!form.toastType)
      newErrors.toastType = localizeFormError(
        t('formField.required'),
        formObject.formSteps[0].coreFields.find(
          (field: any) => field.fieldInputNameMarketoRestApi === 'toastType'
        )?.fieldLabel
      )
    return newErrors
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value ?? '' }))
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setTouched((prev) => ({ ...prev, [name]: true }))

    if (name === 'phone') {
      const formatted = formatPhoneNumber(value)
      setForm((prev) => ({ ...prev, phone: formatted ?? '' }))
    }

    // Create cookies for full name when user fills it out and moves focus away
    if (name === 'mkoFullName' && value && value.trim()) {
      const extractedName = extractFirstNameLastName(value.trim())
      if (extractedName.FirstName) {
        _helpers.createCookie('fN', extractedName.FirstName, 525600, '/')
      }
      if (extractedName.LastName) {
        _helpers.createCookie('lN', extractedName.LastName, 525600, '/')
      }
      if (extractedName.FullName) {
        _helpers.createCookie('fullName', extractedName.FullName, 525600, '/')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({
      mkoFullName: true,
      email: true,
      phone: true,
      company: true,
      postalCode: true,
      toastType: true
    })
    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return
    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
      setIsSubmitted(true)
    }, 10000)

    try {
      const formData = new FormData()

      const mkoFullName = form['mkoFullName']
      // Always ensure extractedName is an object
      const extractedName = mkoFullName
        ? extractFirstNameLastName(mkoFullName)
        : { FirstName: '', LastName: '', FullName: '' }
      for (const value in form) {
        // Skip firstName and lastName as they are handled in mkoFullName block
        // Skip craftInferredCountry as it's added separately with geo data
        if (
          value === 'firstName' ||
          value === 'lastName' ||
          value === 'craftInferredCountry'
        ) {
          continue
        }

        // Use 'as any' for dynamic access
        const fieldValue = (form as any)[value]
        if (value === 'mkoFullName') {
          if (extractedName.FirstName) {
            formData.append('firstName', extractedName.FirstName)
            _helpers.createCookie('fN', extractedName.FirstName, 525600, '/')
          }
          if (extractedName.LastName) {
            formData.append('lastName', extractedName.LastName)
            _helpers.createCookie('lN', extractedName.LastName, 525600, '/')
          }
          if (extractedName.FullName) {
            _helpers.createCookie(
              'fullName',
              extractedName.FullName,
              525600,
              '/'
            )
          }
          // Add mkoFullName to FormData
          formData.append(value, fieldValue)
        } else if (value === 'email') {
          _helpers.createCookie('email', fieldValue, 525600, '/')
        } else if (value === 'company') {
          _helpers.createCookie('company', fieldValue, 525600, '/')
        } else if (value === 'phone') {
          const formattedPhone = formatPhoneNumber(fieldValue) || fieldValue
          _helpers.createCookie('phonenumber', formattedPhone, 525600, '/')
          formData.append(value, formattedPhone)
          continue
        } else if (value === 'postalCode' && fieldValue) {
          _helpers.createCookie('_userUpdatedLoc', fieldValue, 525600, '/')
        } else if (value === 'mkoProgramName') {
          if (
            fieldValue?.toLowerCase() === 'website-demo-request'.toLowerCase()
          ) {
            _helpers.createCookie('toastDemoForm', '1', 525600, '/')
          }
        } else {
          _helpers.createCookie('toastResourceForm', '1', 900, '/')
          _helpers.createCookie('progressiveEnabled', '1', 43200, '/')
        }

        // Only when Toast Type exists
        if ((form as any)['toastType']) {
          let regionString = ''
          if (region !== 'us') {
            regionString = `/${region}`
          }
          if (value === 'returnURL') {
            let returnUrl = `${regionString}/request-demo-confirmed`
            if ((form as any)['toastType'] === "I'm already a Toast customer") {
              returnUrl = `${regionString}/thank-you/toast-confirmed-customer`
            } else if (
              (form as any)['toastType'] === "I'm a restaurant guest" ||
              (form as any)['toastType'] === "I'm a retail guest"
            ) {
              returnUrl = `${regionString}/thank-you/toast-confirmed`
            }
            formData.append(value, returnUrl)
          } else {
            const appendValue = [null, undefined].includes(fieldValue)
              ? ''
              : fieldValue
            formData.append(value, appendValue)
          }
        } else {
          if (
            [
              'blogSubscriber',
              'dataSubscriber',
              'eventsSubscriber',
              'enterpriseNewsletterSubscriber',
              'unsubscribed'
            ].includes(value)
          ) {
            formData.set(value, fieldValue ? 'yes' : 'no')
            continue
          }
          const appendValue = [null, undefined].includes(fieldValue)
            ? ''
            : fieldValue
          formData.append(value, appendValue)
        }
      }
      // Add country
      formData.append(
        'craftInferredCountry',
        _helpers.getCountry(countryCode ?? '')
      )
      // Remove isAjax if not using AJAX
      if (!isAjax) {
        formData.delete('isAjax')
      }

      // // Debug FormData contents properly
      const response = await fetch(formActionUrlState, {
        method: 'POST',
        body: formData
      })

      const heapObject = {
        dev_form_submission: 'submitted'
      }
      heapAnalytics(heapObject)

      setIsSubmitting(false)

      if (response.redirected) {
        setIsSubmitted(true)
        // Force HTTPS for the redirect
        const redirectUrl = response.url.replace('http://', 'https://')
        window.location.href = redirectUrl
        return
      }

      setIsSubmitted(true)
    } catch (err) {
      setIsSubmitting(false)
      setIsError(true)
    }
  }

  return (
    <>
      <form
        aria-label='Main Form'
        className='margin-bottomSM toast-form form-default-only toast-validate 1003-form vertical'
        data-heap-mktform-id={marketoId}
        id={`mktForm_${marketoId}`}
        onSubmit={handleSubmit}
      >
        <div className='full-width'>
          {formObject.formSteps[0].coreFields.map((step: any) => {
            if (step.fieldType !== 'Select (Dropdown)') {
              return (
                <FormField
                  error={errors[step.fieldInputNameMarketoRestApi]}
                  key={step.fieldInputNameMarketoRestApi}
                  label={step.fieldLabel}
                  name={step.fieldInputNameMarketoRestApi}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={
                    form[
                      step.fieldInputNameMarketoRestApi as keyof typeof form
                    ] ?? ''
                  }
                  required={step.isRequired}
                  touched={
                    touched[
                      step.fieldInputNameMarketoRestApi as keyof typeof touched
                    ]
                  }
                />
              )
            } else {
              return (
                <div
                  className='field-wrapper select-wrapper toast-type mb-5 relative'
                  data-conditional-field='false'
                  key={step.fieldInputNameMarketoRestApi}
                >
                  <div className='relative'>
                    <select
                      aria-invalid={!!errors[step.fieldInputNameMarketoRestApi]}
                      aria-label='Which best describes you?'
                      id={step.fieldInputNameMarketoRestApi}
                      name={step.fieldInputNameMarketoRestApi}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      className={classNames(fieldClassSelect, {
                        'text-[#666]':
                          !form[
                            step.fieldInputNameMarketoRestApi as keyof typeof form
                          ],
                        'text-[#222]':
                          !!form[
                            step.fieldInputNameMarketoRestApi as keyof typeof form
                          ],
                        [fieldClassError]:
                          touched[
                            step.fieldInputNameMarketoRestApi as keyof typeof touched
                          ] && errors[step.fieldInputNameMarketoRestApi]
                      })}
                      style={{
                        backgroundImage:
                          "url('https://d2w1ef2ao9g8r9.cloudfront.net/images/icons/down-arrow-grey.svg')",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 15px bottom 16px'
                      }}
                      value={
                        form[
                          step.fieldInputNameMarketoRestApi as keyof typeof form
                        ] ?? ''
                      }
                    >
                      <option value='' disabled>
                        {step.fieldLabel}
                        {step.required ? '*' : ''}
                      </option>
                      {step.fieldOptions.map((opt: any) => {
                        return (
                          <option
                            disabled={opt.disabled}
                            key={opt.value}
                            value={opt.value}
                          >
                            {opt.key}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <label
                    className='hidden'
                    htmlFor={step.fieldInputNameMarketoRestApi}
                    style={{ display: 'none' }}
                  >
                    {step.fieldLabel}
                  </label>
                  {touched[
                    step.fieldInputNameMarketoRestApi as keyof typeof touched
                  ] && errors[step.fieldInputNameMarketoRestApi] ? (
                    <span className={errorClass}>
                      {errors[step.fieldInputNameMarketoRestApi]}
                    </span>
                  ) : null}
                </div>
              )
            }
          })}
          <div className='submit-wrapper center' id=''>
            <button
              className='w-full bg-blue-75 text-white rounded-[6px] py-3 px-4 text-base font-semibold shadow-sm hover:bg-blue-100 focus:outline-none transition-all'
              data-submit-conversion-event=''
              disabled={isSubmitting}
              id='mktFrmSubmit'
              name='submitButton'
              type='submit'
              style={{
                width: '100%',
                backgroundColor: isSubmitting ? '#2583e3' : ''
              }}
            >
              {isSubmitting ? (
                <div className='flex items-center justify-center'>
                  <img
                    alt={t('Loading...')}
                    className='w-5 h-5 mr-2'
                    src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
                  />
                </div>
              ) : (
                formObject.formSteps[0].submitLabel ?? 'Submit'
              )}
            </button>
          </div>
        </div>
        {renderHiddenFields(form)}
        <input
          id='action'
          name='action'
          type='hidden'
          value={formActionUrlState}
        />
      </form>
      <div className='' data-testid='MainForm-disclaimer'>
        {renderRichText(
          formObject.disclaimer,
          FormDisclaimerOptionsRequestDemo
        )}
      </div>
    </>
  )
}

export default RequestDemoForm
